import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { getMe, type Me } from '../lib/api.ts';

// Pathless layout route — children live at /_authenticated/* but render at
// the URL without the underscore prefix. beforeLoad gates every child.
//
// Auth check uses TanStack Query to cache the /auth/me result so subsequent
// navigations don't re-fetch within the staleTime window.
export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient
      .fetchQuery<Me>({
        queryKey: ['auth', 'me'],
        queryFn: async () => {
          const res = await getMe();
          if (!res.ok) throw new Error(res.error.code);
          return res.data;
        },
        staleTime: 5 * 60 * 1000, // 5 min — re-check session every nav after staleness
      })
      .catch(() => null);

    if (!me) {
      throw redirect({
        to: '/login',
        // Preserve where the user was trying to go so we can land them back
        // after sign-in. Passed through the OAuth state via the API worker.
        search: { redirect: location.href },
      });
    }

    // Make the user payload available to child routes via Route.useRouteContext().
    return { user: me.user, session: me.session };
  },
  component: () => <Outlet />,
});
